import { useState, useEffect, useRef } from 'react';
import Services from '../services/Services';
import { EventDetails } from '../types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { useGeneral } from '../contexts';
import { getPublicToken } from '../utils/publicToken';

interface UseEventDetailsOptions {
  token: string | null;
  eventID: string;
  loadingKey?: string; // Optional key for centralized loading state
}

export const useEventDetails = (options: UseEventDetailsOptions) => {
  const { token, eventID, loadingKey } = options;
  const { setLoading, isLoading } = useGeneral();
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const isMountedRef = useRef(true);
  
  // Use centralized loading if key is provided, otherwise use local state
  const loading = loadingKey ? isLoading(loadingKey) : localLoading;
  
  const refreshEvent = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    registerAbortController(abortController);

    const fetchEventDetails = async () => {
      // Check if eventID is valid - if not, clear state and return early
      // Check for both null/undefined and empty string
      if (!eventID || (typeof eventID === 'string' && eventID.trim() === '') || isLoggingOut()) {
        if (isMountedRef.current) {
          // Only clear loading if we don't have a valid eventID
          // This prevents clearing loading when transitioning from empty to valid
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
          // Clear event and error when eventID is invalid
          setEvent(null);
          setError(null);
        }
        return;
      }

      // Set loading to true immediately when we have a valid eventID
      // This ensures loading state is correct even if eventID was previously empty
      if (isMountedRef.current) {
        if (loadingKey) {
          setLoading(loadingKey, true);
        } else {
          setLocalLoading(true);
        }
        setError(null);
      }

      // Get token - use user token if available, otherwise get public token
      let effectiveToken = token;
      if (!effectiveToken) {
        effectiveToken = await getPublicToken();
      }

      if (!effectiveToken) {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
          setError('Unable to fetch event details. Please try again later.');
        }
        return;
      }

      try {
        const [eventResponse, ticketsResponse] = await Promise.all([
          Services.SocialHiveGetEventDetails(effectiveToken, eventID, abortController),
          Services.SocialHiveGetEventSoldTickets(effectiveToken, eventID, abortController)
        ]);

        // Check if request was aborted or component unmounted
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (eventResponse.data.status && eventResponse.data.result) {
          setEvent(eventResponse.data.result);
        } else {
          setError(eventResponse.data.message || 'Failed to fetch event details');
        }

        if (ticketsResponse.data.status && ticketsResponse.data.result) {
          setParticipantCount(ticketsResponse.data.result.length);
        }
      } catch (err: any) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message?.includes('Logout in progress')) {
          return;
        }

        if (isMountedRef.current) {
          setError(err.message || 'An error occurred');
          setEvent(null);
        }
      } finally {
        if (isMountedRef.current && !abortController.signal.aborted) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
        } else if (loadingKey) {
          // Clear loading even if unmounted to prevent stuck state
          setLoading(loadingKey, false);
        }
      }
    };

    fetchEventDetails();

    return () => {
      abortController.abort();
      unregisterAbortController(abortController);
      // Clear loading state on cleanup to prevent stuck state
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  }, [token, eventID, loadingKey, setLoading, refreshTrigger]);

  return { event, loading, error, participantCount, refreshEvent };
};

